/**
 * YouTube Converter - Converts legacy placeholder content to working iframes
 */

/**
 * Extracts YouTube video ID from various YouTube URL formats
 */
function extractYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  url = url.trim();
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }

  return null;
}

/**
 * Generates YouTube embed URL with proper parameters
 */
function generateYouTubeEmbedUrl(videoId, options = {}) {
  if (!videoId) {
    throw new Error('Video ID is required');
  }

  const baseUrl = 'https://www.youtube.com/embed/';
  const params = [];

  const defaultOptions = {
    autoplay: 0,
    rel: 0,
    modestbranding: 1,
    enablejsapi: 1
  };

  const finalOptions = { ...defaultOptions, ...options };

  Object.entries(finalOptions).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return `${baseUrl}${videoId}${params.length > 0 ? '?' + params.join('&') : ''}`;
}

/**
 * Converts legacy YouTube placeholder elements to working iframes
 */
export function convertLegacyYouTubePlaceholders() {
  // Find all YouTube embed containers with placeholder content
  const containers = document.querySelectorAll('.youtube-embed-container');
  
  containers.forEach(container => {
    const videoId = container.getAttribute('data-youtube-id');
    const title = container.getAttribute('data-youtube-title') || 'YouTube video player';
    
    if (videoId && container.querySelector('.youtube-placeholder')) {
      // This is a legacy placeholder, convert it to iframe
      convertPlaceholderToIframe(container, videoId, title);
    }
  });
}

/**
 * Converts a single placeholder container to iframe
 */
function convertPlaceholderToIframe(container, videoId, title) {
  const embedUrl = generateYouTubeEmbedUrl(videoId);
  
  // Create new iframe HTML
  const iframeHTML = `
    <iframe 
      src="${embedUrl}" 
      title="${title}"
      style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;" 
      frameborder="0" 
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
      allowfullscreen>
    </iframe>
  `;
  
  // Replace the content
  container.innerHTML = iframeHTML;
  
  // Ensure proper styling
  container.style.cssText = 'position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 16px 0;';
}

/**
 * Initialize YouTube conversion when DOM is ready
 */
export function initializeYouTubeConverter() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', convertLegacyYouTubePlaceholders);
  } else {
    convertLegacyYouTubePlaceholders();
  }
}

// Auto-initialize if this script is loaded
if (typeof window !== 'undefined') {
  initializeYouTubeConverter();
} 